import { parseISO } from 'date-fns'
import { useMemo } from 'react'
import { useContextSelector } from 'use-context-selector'
import { HouseInfo } from '@components/molecules'
import { IconButton, Typography, Stack, List } from '@mui/material'
import { OpenDataResourceEnum } from '@enums/OpenDataResourceEnum'
import { capitalize } from '@utils/functions/capitalize/capitalize'
import { getSortedBucket } from '@utils/functions/buckets/getSortedBucket'
import {
  formatDate,
  formatTimeHourMinute,
  formatterHighlight,
} from '@utils/functions/formatter/formatter'

import {
  Close,
  RoomOutlined,
  CalendarTodayRounded,
  AccessTimeOutlined,
  LaunchOutlined,
  StickyNote2Outlined,
} from '@mui/icons-material'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'

import {
  ModalContainer,
  ModalToolbar,
  ModalContent,
  Title,
  Separator,
  ContentIconText,
  TextLabel,
  TextDescription,
  TextParagraph,
  TextChip,
  FilterListVirtualized,
  FilterListVirtualizedItem,
  ContentColumn,
  ModalFooter,
  TextFooter,
  ContentHeader,
  ContentEventInfo,
} from './styles'
import _ from 'lodash'
import { EventModalProps } from './types'
import { useModal } from '@contexts/Modal'
import { MonitoredPropositionScheduled } from './components/MonitoredPropositionScheduled'
import { HighlightedPropositionScheduled } from './components/HighlightedPropositionScheduled'
import { TextLink } from '@components/atoms'
import TrashCanIcon from '@components/icons/TrashCan/TrashCan'
import { EventModalContext } from './context'

export function EventModalView({ event }: EventModalProps) {
  const query = new URLSearchParams(document.location.search)
  const keyword = query.get('keyword')
  const buckets = query.get('buckets')
  const { cleanStack } = useModal()
  const handleRemoveEvent = useContextSelector(
    EventModalContext,
    (ctx) => ctx.handleRemoveEvent
  )
  const handleSaveOrMonitorEvent = useContextSelector(
    EventModalContext,
    (ctx) => ctx.handleSaveOrMonitorEvent
  )

  const params = `?buckets=${getSortedBucket(
    buckets || '',
    'events'
  )}&keyword=${keyword}`

  const handleOpenEvent = () => {
    window.open(`/events/${event?.id}${params}`, '_blank')
  }

  const date = formatDate(event.dateStart)

  const weekday = !event.dateStart
    ? '--'
    : parseISO(event.dateStart).toLocaleDateString('pt-BR', {
        weekday: 'long',
      })

  const hour = formatTimeHourMinute(event.dateStart)

  const description = useMemo(() => {
    return formatterHighlight(event, ['description'], 'Não Informado')
  }, [])

  const hasSavedEvent = !!event.savedEvents?.some(
    (savedEvent) => savedEvent.active
  )

  const handleSaveOrMonitorEventClick = async () => {
    await handleSaveOrMonitorEvent(hasSavedEvent)
  }

  const hasUserEvents = event.userEvents?.length
    ? event.userEvents.length > 0
    : false

  const isPropositionMonitored = !!(
    event.savedPropositions && event.savedPropositions.length > 0
  )

  let HeadColor = isPropositionMonitored ? '#ED6D47' : `#F9FAFC`
  let HeadText = isPropositionMonitored
    ? `${event.savedPropositions?.length} proposições monitoradas em pauta`
    : ``

  HeadColor =
    event.highlights && event.highlights?.length > 0 ? '#FFB829' : HeadColor
  HeadText =
    event.highlights && event.highlights?.length > 0
      ? `${event.highlights.length} palavra-chave monitoradas encontradas`
      : HeadText

  const iconButtonColor = isPropositionMonitored ? '#FFFFFF' : '#606162'

  return (
    <ModalContainer>
      <ModalToolbar
        sx={{
          backgroundColor: HeadColor,
        }}
      >
        <Typography
          variant="$font-body-base"
          color="$color-text-light"
          sx={{ fontWeight: 500, lineHeight: '21px' }}
        >
          {HeadText}
        </Typography>
        <Stack direction="row">
          <IconButton onClick={() => handleOpenEvent()}>
            <LaunchOutlined sx={{ color: iconButtonColor, fontSize: '16px' }} />
          </IconButton>

          <IconButton onClick={() => cleanStack()}>
            <Close sx={{ color: iconButtonColor, fontSize: '16px' }} />
          </IconButton>
        </Stack>
      </ModalToolbar>

      <ModalContent>
        <ContentHeader>
          <Title>
            {[
              event?.organs?.[0]?.name,
              event?.type ||
                _.get(
                  OpenDataResourceEnum,
                  event?.openDataResource,
                  'Não informado'
                ),
            ]
              .filter((i) => !!i)
              .join(' - ')}
          </Title>
          {event?.situation?.length && (
            <TextChip
              variant="outlined"
              label={event.situation}
              color="primary"
            />
          )}
        </ContentHeader>

        <ContentEventInfo>
          <ContentIconText>
            <HouseInfo
              text={event.openDataResource}
              sx={{ color: '#202122' }}
            />
          </ContentIconText>

          <ContentIconText>
            <RoomOutlined sx={{ color: '#202122', fontSize: '20px' }} />

            <TextDescription>
              {event.local?.name || 'Não Informado'}
            </TextDescription>
          </ContentIconText>

          <ContentIconText>
            <CalendarTodayRounded sx={{ color: '#202122', fontSize: '20px' }} />

            <TextDescription>
              {capitalize(weekday)} - {date}
            </TextDescription>
          </ContentIconText>

          <ContentIconText>
            <AccessTimeOutlined sx={{ color: '#202122', fontSize: '20px' }} />

            <TextDescription>{hour}</TextDescription>
          </ContentIconText>
        </ContentEventInfo>

        <Separator />

        <ContentColumn>
          <TextLabel>Tema:</TextLabel>
          <TextParagraph>{description}</TextParagraph>
        </ContentColumn>

        <Separator />

        <ContentColumn>
          <ContentIconText>
            <StickyNote2Outlined
              sx={{
                color: '#556282',
                fontSize: '20px',
              }}
            />
            <TextDescription>Pauta da agenda</TextDescription>
          </ContentIconText>

          {event?.schedule && event?.schedule.length > 0 ? (
            <FilterListVirtualized
              itemData={event?.schedule}
              innerElementType={List}
              itemCount={event?.schedule.length || 0}
              itemSize={30}
              height={120}
              width={460}
            >
              {({ data, index, style }) => (
                <FilterListVirtualizedItem customStyle={style}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {event.highlights?.length ? (
                      <HighlightedPropositionScheduled
                        event={event}
                        data={data}
                        index={index}
                      />
                    ) : (
                      <MonitoredPropositionScheduled
                        event={event}
                        data={data}
                        index={index}
                      />
                    )}
                    {_.get(data, `${index}.relatedTitle`) && (
                      <Stack spacing={1} direction="row" alignItems="center">
                        <Typography
                          variant="$font-text-link"
                          color="$color-text-secondary"
                        >
                          Apensada a:
                        </Typography>

                        {_.get(data, `${index}.relatedPropositionId`) ? (
                          <TextLink
                            target="_blank"
                            text={_.get(data, `${index}.relatedTitle`)}
                            color="#2970FF"
                            href={`/propositions/${_.get(
                              data,
                              `${index}.relatedPropositionId`
                            )}`}
                          />
                        ) : (
                          <Typography
                            variant="$font-text-link"
                            color="$color-text-secondary"
                          >
                            {_.get(data, `${index}.relatedTitle`)}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </FilterListVirtualizedItem>
              )}
            </FilterListVirtualized>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Não disponível
            </Typography>
          )}
        </ContentColumn>
      </ModalContent>

      <ModalFooter>
        {hasUserEvents ? (
          <TextFooter color="$color-text-primary" onClick={handleRemoveEvent}>
            <TrashCanIcon width={24} height={24} fill="none" stroke="#F03D3D" />
            Remover da agenda
          </TextFooter>
        ) : (
          <TextFooter
            color="$color-text-primary"
            onClick={handleSaveOrMonitorEventClick}
          >
            <PushPinOutlinedIcon
              sx={{
                color: '$color-action-border',
              }}
            />
            {hasSavedEvent ? 'Salvar na Agenda' : 'Monitorar evento'}
          </TextFooter>
        )}
      </ModalFooter>
    </ModalContainer>
  )
}
